import { useState, useEffect } from 'react';

/**
 * LoadingState — US-F12 AC2, AC3
 *
 * Reusable loading-state component. The `message` prop is REQUIRED —
 * capabilities must supply copy like "Getting your latest information...".
 * Never an indefinite spinner.
 *
 * After 8 seconds the component switches to a timeout state with a retry
 * action and a human-language apology (AC3).
 *
 * Usage:
 *   <LoadingState message="Getting your latest information..." onRetry={loadData} />
 */

const DEFAULT_TIMEOUT_MS = 8000;

export default function LoadingState({ message, onRetry, timeout = DEFAULT_TIMEOUT_MS, testId, style = {} }) {
  const [timedOut, setTimedOut] = useState(false);

  // Dev-mode warning if no message (AC4)
  if (process.env.NODE_ENV !== 'production' && !message) {
    console.warn('[LoadingState] Missing required "message" prop. Capabilities must supply human-language copy.');
  }

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), timeout);
    return () => clearTimeout(timer);
  }, [timeout]);

  const displayMessage = message || 'Loading...';

  if (timedOut) {
    return (
      <div style={{ ...styles.container, ...style }} data-testid={testId}>
        <p style={styles.message}>
          This is taking longer than expected — tap to try again.
        </p>
        {onRetry && (
          <button style={styles.retryBtn} onClick={() => { setTimedOut(false); onRetry(); }}>
            Try again
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, ...style }} data-testid={testId}>
      <div style={styles.dots}>
        <span style={styles.dot}></span>
        <span style={{ ...styles.dot, animationDelay: '0.15s' }}></span>
        <span style={{ ...styles.dot, animationDelay: '0.3s' }}></span>
      </div>
      <p style={styles.message}>{displayMessage}</p>

      <style>{`
        @keyframes loading-dot {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    textAlign: 'center',
    backgroundColor: 'var(--card-bg)',
    borderRadius: '10px',
    boxShadow: 'var(--shadow-card)'
  },
  dots: {
    display: 'flex',
    gap: '6px',
    marginBottom: '18px'
  },
  dot: {
    width: '8px',
    height: '8px',
    backgroundColor: 'var(--teal)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'loading-dot 1.4s infinite ease-in-out both'
  },
  message: {
    fontSize: '14px',
    color: 'var(--gray-600)',
    margin: 0,
    lineHeight: 1.5
  },
  retryBtn: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: 'var(--navy)',
    color: 'var(--white)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer'
  }
};
